/**
 * Camera service for accessing device camera and capturing frames
 * Supports single-frame and multi-frame capture for improved recognition accuracy
 */

import { createScopedReporter } from './errorReporter';
import {
  assessImageQuality,
  getImageDataFromVideo,
  type QualityAssessment
} from './imageQuality';

// Re-export QualityAssessment type for consumers
export type { QualityAssessment } from './imageQuality';

const SERVICE_TAG = '[CAMERA]';
const reporter = createScopedReporter('CAMERA');

/**
 * Center crop ratio applied to captured frames before upload.
 * 1.0 = send full frame (model trained on full-frame images).
 * Previously 0.6 but reverted — the crop was cutting off dice at tray edges.
 */
export const CENTER_CROP_RATIO = 1.0;

export interface CameraOptions {
  facingMode?: 'user' | 'environment';
  deviceId?: string;
  width?: number;
  height?: number;
}

const DEFAULT_OPTIONS: CameraOptions = {
  facingMode: 'environment',
  width: 1920,
  height: 1080
};

/**
 * Multi-frame capture options
 */
export interface MultiFrameCaptureOptions {
  /** Number of frames to capture (default: 3) */
  frameCount?: number;
  /** Interval between frames in milliseconds (default: 200) */
  intervalMs?: number;
  /** JPEG quality 0-1 (default: 0.9) */
  quality?: number;
}

/**
 * Result of multi-frame capture with quality metrics
 */
export interface CapturedFrame {
  /** Base64 JPEG data URL */
  data: string;
  /** Frame capture timestamp */
  timestamp: number;
  /** Quality score 0-1 (higher is better) */
  qualityScore: number;
}

/**
 * Motion detection result
 */
export interface MotionResult {
  /** Motion detected between frames */
  hasMotion: boolean;
  /** Motion magnitude 0-1 (higher = more motion) */
  magnitude: number;
}

/**
 * Start camera stream and attach to video element
 */
export async function startCamera(
  videoElement: HTMLVideoElement,
  options: CameraOptions = {}
): Promise<MediaStream> {
  const { facingMode, deviceId, width, height } = { ...DEFAULT_OPTIONS, ...options };

  try {
    // Build video constraints - deviceId takes precedence over facingMode
    const videoConstraints: MediaTrackConstraints = {
      width: { ideal: width },
      height: { ideal: height }
    };

    if (deviceId) {
      videoConstraints.deviceId = { exact: deviceId };
    } else {
      videoConstraints.facingMode = facingMode;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio: false
    });

    videoElement.srcObject = stream;
    await videoElement.play();

    return stream;
  } catch (error) {
    reporter.error('Failed to access camera', {
      originalError: error instanceof Error ? error : undefined,
      context: { facingMode }
    });
    throw error;
  }
}

/**
 * Stop camera stream and release resources
 */
export function stopCamera(stream: MediaStream): void {
  stream.getTracks().forEach((track) => {
    track.stop();
  });
}

/**
 * Thrown by {@link captureFrame} when the supplied video element is not in a
 * state that can yield a usable frame (TICKET-293).
 *
 * Without this guard a stale element silently returns a valid-looking but
 * blank ~600-byte JPEG, recognition silently degrades, and downstream
 * fallbacks (e.g. `extractFirstFrame`) never engage. Throwing forces callers
 * to either retry, fall back, or surface the failure.
 */
export class CaptureFrameNotReadyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CaptureFrameNotReadyError';
  }
}

/**
 * Capture current frame from video as base64 JPEG.
 *
 * @throws {CaptureFrameNotReadyError} when the video element is not yet
 *   delivering decoded frames (videoWidth/videoHeight < 100, or
 *   readyState < HAVE_CURRENT_DATA).
 * @throws {Error} when a 2D canvas context cannot be created.
 */
export function captureFrame(
  videoElement: HTMLVideoElement,
  quality = 0.9
): string {
  const videoW = videoElement.videoWidth;
  const videoH = videoElement.videoHeight;
  const readyState = videoElement.readyState;

  // Guard against blank/uninitialised video elements. A stale element returns
  // valid-looking but useless JPEGs; better to throw and let the caller fall
  // back to a recorded-blob path. HAVE_CURRENT_DATA = 2.
  if (videoW < 100 || videoH < 100 || readyState < 2) {
    throw new CaptureFrameNotReadyError(
      `Video element not ready (videoWidth=${videoW} videoHeight=${videoH} readyState=${readyState})`
    );
  }

  // Apply center crop to remove tray borders and table edges
  const cropW = Math.round(videoW * CENTER_CROP_RATIO);
  const cropH = Math.round(videoH * CENTER_CROP_RATIO);
  const sx = Math.round((videoW - cropW) / 2);
  const sy = Math.round((videoH - cropH) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    reporter.error('Failed to create canvas context', { showToast: false });
    throw new Error('Failed to get canvas context');
  }

  // Draw only the center crop from the video source
  ctx.drawImage(videoElement, sx, sy, cropW, cropH, 0, 0, cropW, cropH);

  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Check if camera is available on this device
 */
export async function isCameraAvailable(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((device) => device.kind === 'videoinput');
  } catch (error) {
    reporter.warning('Could not enumerate devices', {
      originalError: error instanceof Error ? error : undefined,
      showToast: false
    });
    return false;
  }
}

/**
 * Get list of available cameras
 */
export async function getAvailableCameras(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'videoinput');
  } catch (error) {
    reporter.error('Failed to get available cameras', {
      originalError: error instanceof Error ? error : undefined,
      showToast: false
    });
    return [];
  }
}

/**
 * Capture multiple frames from video element over time
 * Useful for improving recognition accuracy by selecting best frame
 *
 * @param videoElement - Video element to capture from
 * @param options - Multi-frame capture options
 * @returns Array of captured frames with quality scores
 */
export async function captureMultipleFrames(
  videoElement: HTMLVideoElement,
  options: MultiFrameCaptureOptions = {}
): Promise<CapturedFrame[]> {
  const { frameCount = 3, intervalMs = 200, quality = 0.9 } = options;

  console.info(`${SERVICE_TAG} Starting multi-frame capture`, {
    frameCount,
    intervalMs,
    quality
  });

  const frames: CapturedFrame[] = [];

  for (let i = 0; i < frameCount; i++) {
    const data = captureFrame(videoElement, quality);
    const qualityScore = calculateFrameQuality(videoElement);

    frames.push({
      data,
      timestamp: Date.now(),
      qualityScore
    });

    // Wait before capturing next frame (skip wait on last frame)
    if (i < frameCount - 1) {
      await delay(intervalMs);
    }
  }

  console.info(`${SERVICE_TAG} Multi-frame capture complete`, {
    frameCount: frames.length,
    qualityScores: frames.map((f) => f.qualityScore.toFixed(2))
  });

  return frames;
}

/**
 * Select the best frame from multiple captures based on quality score
 *
 * @param frames - Array of captured frames
 * @returns Best frame based on quality score
 */
export function selectBestFrame(frames: CapturedFrame[]): CapturedFrame {
  if (frames.length === 0) {
    throw new Error('No frames to select from');
  }

  const best = frames.reduce((a, b) => (a.qualityScore > b.qualityScore ? a : b));

  console.info(`${SERVICE_TAG} Selected best frame`, {
    selectedQuality: best.qualityScore.toFixed(2),
    allQualities: frames.map((f) => f.qualityScore.toFixed(2))
  });

  return best;
}

/**
 * Detect motion between two frames
 * Uses pixel difference analysis on downsampled images
 *
 * @param frame1 - First frame (data URL)
 * @param frame2 - Second frame (data URL)
 * @returns Motion detection result with magnitude
 */
export async function detectMotion(
  frame1: string,
  frame2: string
): Promise<MotionResult> {
  return new Promise((resolve) => {
    const img1 = new Image();
    const img2 = new Image();
    let loaded = 0;

    const checkComplete = () => {
      loaded++;
      if (loaded < 2) return;

      // Create small canvas for comparison (downsampled for performance)
      const sampleSize = 64;
      const canvas = document.createElement('canvas');
      canvas.width = sampleSize;
      canvas.height = sampleSize;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reporter.warning('Motion detection failed - no canvas context', { showToast: false });
        resolve({ hasMotion: false, magnitude: 0 });
        return;
      }

      // Draw and sample first image
      ctx.drawImage(img1, 0, 0, sampleSize, sampleSize);
      const data1 = ctx.getImageData(0, 0, sampleSize, sampleSize).data;

      // Draw and sample second image
      ctx.drawImage(img2, 0, 0, sampleSize, sampleSize);
      const data2 = ctx.getImageData(0, 0, sampleSize, sampleSize).data;

      // Calculate pixel difference
      let totalDiff = 0;
      const pixelCount = sampleSize * sampleSize;

      for (let i = 0; i < data1.length; i += 4) {
        // Compare RGB values (skip alpha)
        const rDiff = Math.abs(data1[i] - data2[i]);
        const gDiff = Math.abs(data1[i + 1] - data2[i + 1]);
        const bDiff = Math.abs(data1[i + 2] - data2[i + 2]);
        totalDiff += (rDiff + gDiff + bDiff) / 3;
      }

      // Normalize to 0-1 range (255 max diff per pixel)
      const magnitude = totalDiff / (pixelCount * 255);

      // Motion threshold (adjust based on testing)
      const motionThreshold = 0.02;
      const hasMotion = magnitude > motionThreshold;

      console.info(`${SERVICE_TAG} Motion detection result`, {
        magnitude: magnitude.toFixed(4),
        hasMotion,
        threshold: motionThreshold
      });

      resolve({ hasMotion, magnitude });
    };

    img1.onload = checkComplete;
    img2.onload = checkComplete;
    img1.onerror = () => {
      reporter.warning('Motion detection failed - image 1 load error', { showToast: false });
      resolve({ hasMotion: false, magnitude: 0 });
    };
    img2.onerror = () => {
      reporter.warning('Motion detection failed - image 2 load error', { showToast: false });
      resolve({ hasMotion: false, magnitude: 0 });
    };

    img1.src = frame1;
    img2.src = frame2;
  });
}

/**
 * Calculate frame quality score based on comprehensive image analysis
 * Uses the imageQuality service for Laplacian blur detection, brightness, and contrast.
 * Higher scores indicate better image quality for recognition.
 *
 * @param videoElement - Video element to analyze
 * @returns Quality score 0-1 (higher is better)
 */
function calculateFrameQuality(videoElement: HTMLVideoElement): number {
  const imageData = getImageDataFromVideo(videoElement, 128);

  if (!imageData) {
    reporter.warning('Quality calculation failed - using fallback', {
      context: { fallbackValue: 0.5 },
      showToast: false
    });
    return 0.5;
  }

  const assessment = assessImageQuality(imageData);
  return assessment.overallScore;
}

/**
 * Assess the quality of the current video frame for capture suitability
 *
 * @param videoElement - Video element to assess
 * @returns Full quality assessment or null if unable to capture
 */
export function assessCaptureQuality(videoElement: HTMLVideoElement): QualityAssessment | null {
  const imageData = getImageDataFromVideo(videoElement, 256);

  if (!imageData) {
    reporter.error('Cannot assess quality - failed to get image data', { showToast: false });
    return null;
  }

  return assessImageQuality(imageData);
}

/**
 * Helper function for async delay
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if the camera stream supports torch/flashlight control
 * @param stream - The active MediaStream from the camera
 * @returns true if torch is supported, false otherwise
 */
export async function checkTorchSupport(stream: MediaStream): Promise<boolean> {
  try {
    const track = stream.getVideoTracks()[0];
    if (!track) {
      reporter.warning('No video track found for torch check', { showToast: false });
      return false;
    }

    const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
    const supported = capabilities.torch === true;

    console.info(`${SERVICE_TAG} Torch support check`, { supported });
    return supported;
  } catch (error) {
    reporter.warning('Torch capability check failed', {
      originalError: error instanceof Error ? error : undefined,
      showToast: false
    });
    return false;
  }
}

/**
 * Enable or disable the torch/flashlight on the camera
 * @param stream - The active MediaStream from the camera
 * @param enabled - Whether to enable (true) or disable (false) the torch
 * @returns true if the operation succeeded, false otherwise
 */
export async function setTorch(stream: MediaStream, enabled: boolean): Promise<boolean> {
  try {
    const track = stream.getVideoTracks()[0];
    if (!track) {
      reporter.error('Cannot set torch - no video track', { showToast: false });
      return false;
    }

    await track.applyConstraints({
      advanced: [{ torch: enabled } as MediaTrackConstraintSet]
    });

    console.info(`${SERVICE_TAG} Torch ${enabled ? 'enabled' : 'disabled'}`);
    return true;
  } catch (error) {
    reporter.error('Failed to set torch', {
      originalError: error instanceof Error ? error : undefined,
      context: { enabled },
      showToast: false
    });
    return false;
  }
}

/**
 * Trigger camera refocus by briefly stopping and restarting the video track.
 * This forces the camera to re-run its autofocus routine.
 *
 * @param stream - The active MediaStream from the camera
 * @param videoElement - The video element displaying the stream
 * @returns true if refocus was triggered, false otherwise
 */
export async function triggerRefocus(
  stream: MediaStream,
  videoElement: HTMLVideoElement
): Promise<boolean> {
  try {
    const track = stream.getVideoTracks()[0];
    if (!track) {
      reporter.error('Cannot refocus - no video track', { showToast: false });
      return false;
    }

    console.info(`${SERVICE_TAG} Triggering refocus`);

    // Method 1: Try using focusMode constraint if supported
    const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
      focusMode?: string[];
    };

    const supportsContinuous = capabilities.focusMode?.includes('continuous');
    const supportsManual = capabilities.focusMode?.includes('manual');

    if (supportsContinuous && supportsManual) {
      // Toggle focus mode to trigger autofocus
      await track.applyConstraints({
        advanced: [{ focusMode: 'manual' } as MediaTrackConstraintSet]
      });
      await delay(100);
      await track.applyConstraints({
        advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet]
      });
      console.info(`${SERVICE_TAG} Refocus triggered via focusMode toggle`);
      return true;
    }

    // Method 2: Fallback - restart the track to trigger autofocus
    track.enabled = false;
    await delay(150);
    track.enabled = true;

    // Ensure video continues playing
    if (videoElement.paused) {
      await videoElement.play();
    }

    console.info(`${SERVICE_TAG} Refocus triggered via track restart`);
    return true;
  } catch (error) {
    reporter.error('Failed to trigger refocus', {
      originalError: error instanceof Error ? error : undefined,
      showToast: false
    });
    return false;
  }
}
